import { BadgeCheck } from "iconoir-react";
import React, { useState, useEffect, createContext, useContext } from "react";

interface NotificationToastProps {
  message: string;
  type?: "success" | "error" | "info";
  duration?: number;
  onClose?: () => void;
}

export const NotificationToast: React.FC<NotificationToastProps> = ({
  message,
  duration = 3000,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Start animation after component mounts
    setTimeout(() => setIsAnimating(true), 10);

    // Auto hide after duration
    const timer = setTimeout(() => {
      setIsAnimating(false);
      setTimeout(() => {
        setIsVisible(false);
        onClose?.();
      }, 300); // Wait for animation to complete
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300 ease-in-out ${
        isAnimating
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0"
      }`}
    >
      <div className="px-4 py-2 bg-[#E06448] rounded-full inline-flex justify-start items-center gap-2 max-w-[90vw] whitespace-nowrap">
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
          <BadgeCheck
            className="w-[18px] h-[18px] text-[#F4E7DA]"
            fill="none"
          />
        </div>
        <div className="text-[#F4E7DA] text-xs font-normal font-sora leading-none whitespace-nowrap">
          {message}
        </div>
      </div>
    </div>
  );
};

// Context for sharing toast state
interface ToastContextType {
  toasts: Array<{
    id: number;
    message: string;
    type?: "success" | "error" | "info";
  }>;
  showToast: (message: string, type?: "success" | "error" | "info") => void;
  showSuccess: (item: string) => void;
  showError: (item: string) => void;
  showInfo: (message: string) => void;
  showPaymentSuccess: () => void;
  removeToast: (id: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Provider component
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<
    Array<{ id: number; message: string; type?: "success" | "error" | "info" }>
  >([]);

  const showToast = (
    message: string,
    type: "success" | "error" | "info" = "success",
  ) => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  };

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  const showSuccess = (item: string) => {
    showToast(`Cập nhật "${item}" thành công`, "success");
  };

  const showError = (item: string) => {
    showToast(`Cập nhật "${item}" thất bại`, "error");
  };

  const showInfo = (message: string) => {
    showToast(message, "info");
  };

  const showPaymentSuccess = () => {
    showToast("Đã trả thành công", "success");
  };

  const value = {
    toasts,
    showToast,
    showSuccess,
    showError,
    showInfo,
    showPaymentSuccess,
    removeToast,
  };

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
};

// Hook for using toast context
export const useNotificationToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useNotificationToast must be used within a ToastProvider");
  }
  return context;
};

// Toast Container Component
export const NotificationToastContainer: React.FC = () => {
  const { toasts, removeToast } = useNotificationToast();

  return (
    <>
      {toasts.map((toast) => (
        <NotificationToast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </>
  );
};
